<template>
  <div class="wrapper kids-check-new-page">
    <div class="kids-check-new-shell">
      <section class="search-hero">
        <div class="search-hero__inner">
          <div class="search-hero__header">
            <h2 class="search-hero__title">Search iKids List</h2>
            <button
              type="button"
              class="search-hero__report-button"
              @click="openReportModal"
            >
              iKids Report
            </button>
          </div>

          <form class="search-box" @submit.prevent="submitSearch">
            <input
              v-model.trim="searchInput"
              type="text"
              class="search-box__input"
              placeholder="Search by child's name, class, or grade..."
              @keyup.enter.prevent="submitSearch"
            >
            <button
              type="submit"
              class="search-box__button"
              :disabled="isLoadingResults"
            >
              <span class="search-box__button-text">{{ isLoadingResults ? 'Searching...' : 'Search' }}</span>
              <span class="search-box__button-icon" aria-hidden="true">{{ isLoadingResults ? '…' : '⌕' }}</span>
            </button>
            <button
              type="button"
              class="search-box__button search-box__button--secondary"
              :disabled="isLoadingResults && !hasSearched"
              @click="clearSearch"
            >
              <span class="search-box__button-text">Clear</span>
              <span class="search-box__button-icon" aria-hidden="true">×</span>
            </button>
          </form>

        </div>
      </section>

      <section class="results-section">
        <div ref="resultsSection" class="results-scroll-area">
          <div v-if="!hasSearched" class="results-empty">
            Enter a keyword, then press Enter or click Search.
          </div>

          <div v-else-if="isLoadingResults" class="results-loading">
            <b-spinner variant="primary" label="Loading" />
          </div>

          <div v-else-if="items.length === 0" class="results-empty">
            No child matched your search.
          </div>

          <div v-else>
            <div class="kids-grid">
              <article
                v-for="item in items"
                :key="item.id"
                class="kid-card"
              >
                <div class="kid-card__avatar-wrap">
                  <img
                    :src="getPhoto(item.get_photo)"
                    :alt="item.name"
                    class="kid-card__avatar"
                  >
                </div>

                <h3 class="kid-card__name">{{ item.name }}</h3>
                <div class="kid-card__class">{{ item.class_name || '-' }}</div>
                <div class="kid-card__grade">{{ item.grade_name || '-' }}</div>

                <dl class="kid-card__meta">
                  <div class="kid-card__meta-row">
                    <dt>Dad</dt>
                    <dd>{{ item.dad_name || '-' }}</dd>
                  </div>
                  <div class="kid-card__meta-row">
                    <dt>Mom</dt>
                    <dd>{{ item.mom_name || '-' }}</dd>
                  </div>
                  <div class="kid-card__meta-row">
                    <dt>Status</dt>
                    <dd>
                      <span :class="statusClass(item)" class="kid-card__status">
                        {{ statusText(item) }}
                      </span>
                    </dd>
                  </div>
                </dl>

                <div class="kid-card__actions">
                  <button
                    type="button"
                    class="kid-card__action"
                    :class="{
                      'kid-card__action--busy': activeActionKidId === item.id,
                      'kid-card__action--danger': currentAction(item) === 'queued',
                    }"
                    :disabled="isActionDisabled(item.id)"
                    @click="handleCardAction(item)"
                  >
                    {{ activeActionKidId === item.id ? 'Loading...' : actionLabel(item) }}
                  </button>

                  <button
                    v-if="showCancelButton(item)"
                    type="button"
                    class="kid-card__action kid-card__action--secondary"
                    :disabled="isActionDisabled(item.id)"
                    @click="handleCancelAction(item)"
                  >
                    {{ activeActionKidId === item.id ? 'Loading...' : 'Cancel' }}
                  </button>

                  <button
                    v-if="showReprintButton(item)"
                    type="button"
                    class="kid-card__action kid-card__action--outline"
                    :disabled="isActionDisabled(item.id)"
                    @click="handleReprint(item)"
                  >
                    {{ activeActionKidId === item.id ? 'Loading...' : 'Reprint' }}
                  </button>
                </div>
              </article>
            </div>

            <div
              v-if="hasMoreResults"
              ref="scrollSentinel"
              class="results-scroll-sentinel"
            >
              <b-spinner v-if="isAppendingResults" variant="primary" label="Loading more" />
            </div>
          </div>
        </div>

        <div class="results-footer">
          <p class="results-footer__meta">
            Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} items
          </p>
        </div>

      </section>
    </div>

    <alertmodal />

    <div
      v-if="isReportModalOpen"
      class="report-modal"
      @click.self="closeReportModal"
    >
      <div class="report-modal__dialog">
        <div class="report-modal__header">
          <div>
            <h3 class="report-modal__title">iKids Report</h3>
            <p class="report-modal__subtitle">One-time class summary. Use refresh to reload manually.</p>
          </div>
          <button
            type="button"
            class="report-modal__close"
            @click="closeReportModal"
          >
            x
          </button>
        </div>

        <div class="report-modal__actions">
          <button
            type="button"
            class="report-modal__refresh"
            :disabled="isReportLoading || reportRefreshCooldown > 0"
            @click="refreshReportSummary"
          >
            {{ reportRefreshButtonLabel }}
          </button>
        </div>

        <div class="report-modal__body">
          <div v-if="isReportLoading" class="report-modal__state">
            <b-spinner variant="primary" label="Loading report" />
          </div>

          <div v-else-if="reportSummary.length === 0" class="report-modal__state">
            No report data available.
          </div>

          <div v-else class="report-table-wrap">
            <table class="report-table">
              <thead>
                <tr class="report-table__group-row">
                  <th rowspan="2" class="report-table__class-head">Class</th>
                  <th colspan="3">Service 1</th>
                  <th colspan="3" class="report-table__service2-head">Service 2</th>
                </tr>
                <tr>
                  <th>In</th>
                  <th>Queued</th>
                  <th>Out</th>
                  <th class="report-table__service2-head">In</th>
                  <th>Queued</th>
                  <th>Out</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in reportSummary" :key="row.id || row.name">
                  <td>{{ row.name }}</td>
                  <td>{{ row.count_check_in }}</td>
                  <td>{{ row.count_queued }}</td>
                  <td>{{ row.count_check_out }}</td>
                  <td class="report-table__service2-col">{{ row.count2_check_in }}</td>
                  <td>{{ row.count2_queued }}</td>
                  <td>{{ row.count2_check_out }}</td>
                </tr>
                <tr class="report-table__total">
                  <th>Total</th>
                  <th>{{ totalReportCheckIn }}</th>
                  <th>{{ totalReportQueued }}</th>
                  <th>{{ totalReportCheckOut }}</th>
                  <th class="report-table__service2-col">{{ totalReportCheckIn2 }}</th>
                  <th>{{ totalReportQueued2 }}</th>
                  <th>{{ totalReportCheckOut2 }}</th>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import EventBus from '@/eventBus'
import alertmodal from '@/views/alert/modal.vue'
import ikidV2 from '@/services/ikid_v2'
import moment from 'moment'

const SEARCH_LIMIT = 20
const DEMO_IKID_IDS = [1467, 1501] // keep in sync with ifgfapi .env IKID_DEMO_IDS

export default {
  name: 'KidsCheckNew',
  components: {
    alertmodal,
  },
  data() {
    return {
      searchInput: '',
      submittedKeyword: '',
      searchDebounceTimer: null,
      items: [],
      meta: {
        total: 0,
        current_page: 1,
        per_page: SEARCH_LIMIT,
        from: 0,
        to: 0,
      },
      hasSearched: false,
      isLoadingResults: false,
      isAppendingResults: false,
      isActionInFlight: false,
      activeActionKidId: null,
      isReportModalOpen: false,
      isReportLoading: false,
      reportSummary: [],
      reportHasLoaded: false,
      reportRefreshCooldown: 0,
      reportRefreshTimer: null,
      scrollObserver: null,
      previousSidebarShow: null,
      layoutNodes: {
        app: null,
        container: null,
        main: null,
        body: null,
      },
    }
  },
  computed: {
    hasMoreResults() {
      return this.items.length < this.meta.total
    },
    totalReportCheckIn() {
      return this.reportSummary.reduce((total, item) => total + Number(item.count_check_in || 0), 0)
    },
    totalReportQueued() {
      return this.reportSummary.reduce((total, item) => total + Number(item.count_queued || 0), 0)
    },
    totalReportCheckOut() {
      return this.reportSummary.reduce((total, item) => total + Number(item.count_check_out || 0), 0)
    },
    totalReportCheckIn2() {
      return this.reportSummary.reduce((total, item) => total + Number(item.count2_check_in || 0), 0)
    },
    totalReportQueued2() {
      return this.reportSummary.reduce((total, item) => total + Number(item.count2_queued || 0), 0)
    },
    totalReportCheckOut2() {
      return this.reportSummary.reduce((total, item) => total + Number(item.count2_check_out || 0), 0)
    },
    reportRefreshButtonLabel() {
      if (this.isReportLoading) return 'Refreshing...'
      if (this.reportRefreshCooldown > 0) return `Refresh (${this.reportRefreshCooldown}s)`
      return 'Refresh'
    },
  },
  mounted() {
    this.attachLayoutClass()
    this.hideSidebarForPage()
    this.setupScrollObserver()
  },
  beforeDestroy() {
    this.restoreSidebarVisibility()
    this.detachLayoutClass()
    this.teardownScrollObserver()
    this.clearSearchDebounce()
    this.clearReportRefreshTimer()
  },
  beforeRouteLeave(to, from, next) {
    this.restoreSidebarVisibility()
    this.detachLayoutClass()
    this.clearSearchDebounce()
    this.clearReportRefreshTimer()
    next()
  },
  watch: {
    searchInput(value) {
      const keyword = (value || '').trim()
      this.clearSearchDebounce()

      if (keyword === '') {
        this.resetSearchState()
        return
      }

      if (keyword.length < 3) return

      this.searchDebounceTimer = window.setTimeout(() => {
        if (keyword === (this.submittedKeyword || '').trim()) return
        this.runSearch(keyword)
      }, 500)
    },
  },
  methods: {
    hideSidebarForPage() {
      if (!this.$store) return
      this.previousSidebarShow = this.$store.state.sidebarShow
      this.$store.commit('set', ['sidebarShow', false])
    },
    restoreSidebarVisibility() {
      if (!this.$store || this.previousSidebarShow === null) return
      this.$store.commit('set', ['sidebarShow', this.previousSidebarShow])
      this.previousSidebarShow = null
    },
    attachLayoutClass() {
      const app = this.$el && this.$el.closest ? this.$el.closest('.c-app') : null
      const container = this.$el && this.$el.closest ? this.$el.closest('.container-fluid') : null
      const main = this.$el && this.$el.closest ? this.$el.closest('.c-main') : null
      const body = this.$el && this.$el.closest ? this.$el.closest('.c-body') : null
      this.layoutNodes = { app, container, main, body }
      if (app) {
        app.classList.add('kids-check-new-app')
      }
      if (container) {
        container.classList.add('kids-check-new-container')
      }
      if (main) {
        main.classList.add('kids-check-new-main')
      }
      if (body) {
        body.classList.add('kids-check-new-body')
      }
    },
    detachLayoutClass() {
      const { app, container, main, body } = this.layoutNodes || {}
      if (app) {
        app.classList.remove('kids-check-new-app')
      }
      if (container) {
        container.classList.remove('kids-check-new-container')
      }
      if (main) {
        main.classList.remove('kids-check-new-main')
      }
      if (body) {
        body.classList.remove('kids-check-new-body')
      }
      this.layoutNodes = {
        app: null,
        container: null,
        main: null,
        body: null,
      }
    },
    getPhoto(data) {
      if (data && data.filename) {
        return axios.defaults.baseURL + '/getfile/memberphoto/' + data.filename
      }
      return axios.defaults.baseURL + '/getfile/memberphoto/no_photo.jpg'
    },
    submitSearch() {
      const keyword = (this.searchInput || '').trim()
      this.clearSearchDebounce()
      this.runSearch(keyword)
    },
    clearSearch() {
      this.clearSearchDebounce()
      this.searchInput = ''
      this.resetSearchState()
    },
    clearSearchDebounce() {
      if (this.searchDebounceTimer) {
        window.clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = null
      }
    },
    resetSearchState() {
      this.submittedKeyword = ''
      this.hasSearched = false
      this.items = []
      this.isLoadingResults = false
      this.isAppendingResults = false
      this.meta = {
        total: 0,
        current_page: 1,
        per_page: SEARCH_LIMIT,
        from: 0,
        to: 0,
      }
    },
    runSearch(keyword) {
      this.submittedKeyword = keyword
      this.meta.current_page = 1
      this.hasSearched = keyword !== ''
      return this.fetchResults({ reset: true })
    },
    openReportModal() {
      this.isReportModalOpen = true
      if (!this.reportHasLoaded) {
        this.loadReportSummary()
      }
    },
    closeReportModal() {
      this.isReportModalOpen = false
    },
    refreshReportSummary() {
      if (this.isReportLoading || this.reportRefreshCooldown > 0) return
      this.startReportRefreshCooldown()
      this.loadReportSummary()
    },
    startReportRefreshCooldown() {
      this.clearReportRefreshTimer()
      this.reportRefreshCooldown = 5
      this.reportRefreshTimer = window.setInterval(() => {
        if (this.reportRefreshCooldown <= 1) {
          this.clearReportRefreshTimer()
          return
        }
        this.reportRefreshCooldown -= 1
      }, 1000)
    },
    clearReportRefreshTimer() {
      if (this.reportRefreshTimer) {
        window.clearInterval(this.reportRefreshTimer)
        this.reportRefreshTimer = null
      }
      this.reportRefreshCooldown = 0
    },
    loadReportSummary() {
      this.isReportLoading = true
      return ikidV2.getKidsCheckManual({
        list: {
          per_page: 1,
          kid_class_id: 0,
          checkin_status: 0,
          search: '',
        },
        include_summary: true,
        use_delta: false,
        summaryFilter: this.$route.query.Pud || null,
      }).then((res) => {
        const summary = res && res.summary && Array.isArray(res.summary.data)
          ? res.summary.data
          : []
        this.reportSummary = summary
        this.reportHasLoaded = true
      }).catch((err) => {
        this.showError(err, 'Failed to load iKids report')
      }).finally(() => {
        this.isReportLoading = false
      })
    },
    fetchResults(options = {}) {
      const reset = options.reset === true
      const page = options.page || this.meta.current_page
      if (!this.hasSearched || this.submittedKeyword === '') {
        this.items = []
        this.meta = {
          ...this.meta,
          total: 0,
          current_page: 1,
          per_page: SEARCH_LIMIT,
          from: 0,
          to: 0,
        }
        return Promise.resolve()
      }

      if (reset) {
        this.isLoadingResults = true
      } else {
        this.isAppendingResults = true
      }

      return ikidV2.searchCheckinCheckout({
        search: this.submittedKeyword,
        page,
        limit: SEARCH_LIMIT,
      }).then((res) => {
        const nextItems = Array.isArray(res.items) ? res.items : []
        if (reset || page === 1) {
          this.items = nextItems
        } else {
          const seenIds = new Set(this.items.map((item) => item.id))
          this.items = this.items.concat(nextItems.filter((item) => !seenIds.has(item.id)))
        }
        this.meta = {
          total: res.meta && res.meta.total ? res.meta.total : 0,
          current_page: res.meta && res.meta.current_page ? res.meta.current_page : page,
          per_page: res.meta && res.meta.per_page ? res.meta.per_page : SEARCH_LIMIT,
          from: this.items.length > 0 ? 1 : 0,
          to: this.items.length,
        }
      }).catch((err) => {
        this.showError(err, 'Failed to search iKids list')
      }).finally(() => {
        this.isLoadingResults = false
        this.isAppendingResults = false
      })
    },
    setupScrollObserver() {
      if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return
      this.scrollObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (!entry || !entry.isIntersecting) return
        this.loadNextPage()
      }, {
        root: this.$refs.resultsSection || null,
        rootMargin: '0px 0px 240px 0px',
        threshold: 0,
      })
      this.$nextTick(() => {
        this.observeSentinel()
      })
    },
    observeSentinel() {
      if (!this.scrollObserver) return
      this.scrollObserver.disconnect()
      if (this.$refs.scrollSentinel) {
        this.scrollObserver.observe(this.$refs.scrollSentinel)
      }
    },
    teardownScrollObserver() {
      if (!this.scrollObserver) return
      this.scrollObserver.disconnect()
      this.scrollObserver = null
    },
    loadNextPage() {
      if (!this.hasSearched || this.isLoadingResults || this.isAppendingResults || this.isActionInFlight || !this.hasMoreResults) {
        return
      }
      const nextPage = this.meta.current_page + 1
      this.meta.current_page = nextPage
      this.fetchResults({ page: nextPage })
    },
    currentAction(item) {
      return item && item.checkin_status ? item.checkin_status.action : null
    },
    actionLabel(item) {
      const action = this.currentAction(item)
      if (action === 'check in') return 'Present'
      if (action === 'queued') return 'Check Out'
      if (action === 'in class') return 'Queue'
      return 'Check In'
    },
    statusText(item) {
      const action = this.currentAction(item)
      if (!action) return 'Ready For Check In'
      if (action === 'check in') return 'Ready For Present'
      if (action === 'queued') return 'Queued'
      if (action === 'in class') return 'In Class'
      if (action === 'check out') return 'Checked Out'
      return action
    },
    statusClass(item) {
      const action = this.currentAction(item)
      if (!action) return 'kid-card__status--default'
      if (action === 'check in') return 'kid-card__status--info'
      if (action === 'queued') return 'kid-card__status--warning'
      if (action === 'in class') return 'kid-card__status--success'
      if (action === 'check out') return 'kid-card__status--muted'
      return 'kid-card__status--default'
    },
    isActionDisabled(kidId) {
      if (!this.isActionInFlight) return false
      return this.activeActionKidId !== kidId
    },
    showCancelButton(item) {
      return this.currentAction(item) === 'in class'
    },
    showReprintButton(item) {
      return this.currentAction(item) === 'in class'
    },
    getActionSteps(item) {
      const action = this.currentAction(item)
      if (action === 'check in') return [2]
      if (action === 'queued') return [4]
      if (action === 'in class') return [3]
      if ((!action || action === 'check out') && DEMO_IKID_IDS.includes(item.id)) return [1]
      return [1, 2]
    },
    getCancelSteps(item) {
      const action = this.currentAction(item)
      if (action === 'in class') return [2, 1]
      return []
    },
    async handleCardAction(item) {
      if (this.isActionInFlight) return

      const steps = this.getActionSteps(item)
      this.isActionInFlight = true
      this.activeActionKidId = item.id

      try {
        for (const status of steps) {
          const res = await ikidV2.changeStatusKidPortal({
            ikid_id: item.id,
            status,
            from: 'portal',
            source_channel: 'portal_kids_check',
          })
          this.patchKidFromResponse(item.id, res)
        }
        if (steps.length === 2 && steps[0] === 1 && steps[1] === 2) {
          await this.printKidCard(item.uuid)
        }
      } catch (err) {
        this.showError(err, 'Failed to update kid status')
      } finally {
        this.activeActionKidId = null
        this.isActionInFlight = false
      }
    },
    async handleCancelAction(item) {
      if (this.isActionInFlight) return

      const steps = this.getCancelSteps(item)
      if (steps.length === 0) return

      this.isActionInFlight = true
      this.activeActionKidId = item.id

      try {
        for (const status of steps) {
          const res = await ikidV2.cancelStatusKidPortal({
            ikid_id: item.id,
            status,
            from: 'portal',
            source_channel: 'portal_kids_check',
          })
          this.patchKidFromResponse(item.id, res)
        }
      } catch (err) {
        this.showError(err, 'Failed to cancel kid status')
      } finally {
        this.activeActionKidId = null
        this.isActionInFlight = false
      }
    },
    async handleReprint(item) {
      if (this.isActionInFlight) return

      this.isActionInFlight = true
      this.activeActionKidId = item.id

      try {
        await this.printKidCard(item.uuid)
      } catch (err) {
        this.showError(err, 'Failed to reprint kid card')
      } finally {
        this.activeActionKidId = null
        this.isActionInFlight = false
      }
    },
    async printKidCard(kidId) {
      const res = await ikidV2.getIkidbyID(kidId)
      const kid = res && res.data ? res.data : null
      if (!kid) {
        throw new Error('Kid detail not found')
      }

      await this.connectToPrinter(
        kid.firstname,
        kid.lastname,
        kid.ikid_class ? kid.ikid_class.name : '',
        kid.barcode
      )
    },
    async connectToPrinter(firstname, lastname, classId, barcode) {
      const checkTime = moment().format('DD/MM/YYYY HH:mm:ss')

      try {
        await axios.post('http://localhost:3000/print', {
          firstname,
          lastname,
          class_id: classId,
          check_time: checkTime,
          barcode,
        }, {
          timeout: 5000,
        })
      } catch (error) {
        console.log('Error connecting to printer:', error)
        console.log('Barcode:', barcode)
      }
    },
    patchKidFromResponse(kidId, response) {
      const kid = response && response.kid ? response.kid : null
      if (!kid) return

      const index = this.items.findIndex((item) => item.id === kidId)
      if (index === -1) return

      const status = Number(kid.status || 0)
      const nextStatus = status > 0 ? {
        action: kid.status_label,
        log_time: kid.log_time,
      } : null

      const updatedItem = {
        ...this.items[index],
        check_status: status > 0 ? status : null,
        checkin_status: nextStatus,
      }

      this.$set(this.items, index, updatedItem)
    },
    showError(err, fallbackMessage) {
      const message = err && err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : fallbackMessage
      EventBus.$emit('OPEN_MODAL_ALERT', {
        title: 'Error',
        mode: 'danger',
        data: message,
      })
    },
  },
  updated() {
    this.observeSentinel()
  },
}
</script>

<style>
.kids-check-new-app .c-subheader {
  display: none !important;
}

.kids-check-new-app .c-footer {
  display: none !important;
}

.kids-check-new-body {
  display: flex !important;
  flex-direction: column !important;
  height: 100vh !important;
  overflow: hidden !important;
}

.kids-check-new-main {
  display: flex !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow: hidden !important;
  padding-top: 0 !important;
}

.kids-check-new-container {
  display: flex !important;
  flex: 1 1 auto !important;
  flex-direction: column !important;
  min-height: 0 !important;
  height: 100% !important;
  overflow: hidden !important;
  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
}
</style>

<style scoped>
.kids-check-new-page {
  height: 100%;
  padding: 0 !important;
  margin: 0 !important;
  background:
    radial-gradient(circle at top, rgba(47, 109, 201, 0.12), transparent 35%),
    linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);
}

.kids-check-new-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

.search-hero {
  flex: 0 0 auto;
  z-index: 5;
  padding: 12px 16px 10px;
  background:
    linear-gradient(180deg, rgba(247, 251, 255, 0.98) 0%, rgba(238, 244, 251, 0.94) 100%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(199, 215, 236, 0.8);
}

.search-hero__inner {
  max-width: 1040px;
  margin: 0 auto;
  text-align: center;
}

.search-hero__header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
  position: relative;
}

.search-hero__report-button {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  min-width: 124px;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid #bcd0e7;
  border-radius: 999px;
  background: #fff;
  color: #1a5d98;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(17, 66, 112, 0.08);
}

.search-hero__report-button:hover {
  opacity: 0.92;
}

.search-hero__title {
  margin: 0;
  color: #16375d;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 820px;
  margin: 0 auto;
}

.search-box__input {
  flex: 1;
  min-height: 48px;
  padding: 0 18px;
  border: 1px solid #c7d7ec;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(20, 63, 108, 0.08);
  color: #17324d;
  font-size: 15px;
  outline: none;
}

.search-box__input::placeholder {
  color: #9aa9bb;
}

.search-box__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 104px;
  min-height: 46px;
  border: none;
  border-radius: 999px;
  background: #1367b4;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.search-box__button-text {
  display: inline;
}

.search-box__button-icon {
  display: none;
  font-size: 18px;
  line-height: 1;
}

.search-box__button:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.search-box__button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.search-box__button--secondary {
  background: #fff;
  border: 1px solid #c7d7ec;
  color: #315777;
}

.results-section {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.results-scroll-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
}

.results-empty,
.results-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 220px;
  border: 1px dashed #c9d6e6;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.72);
  color: #5f7691;
  font-size: 18px;
}

.kids-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
  align-content: start;
}

.kid-card {
  display: flex;
  flex-direction: column;
  padding: 28px 24px 24px;
  border: 1px solid #cfdae8;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 18px 36px rgba(18, 57, 99, 0.08);
}

.kid-card__avatar-wrap {
  display: flex;
  justify-content: center;
}

.kid-card__avatar {
  width: 98px;
  height: 98px;
  border: 5px solid #eef3f8;
  border-radius: 50%;
  object-fit: cover;
  background: #f5f8fc;
}

.kid-card__name {
  margin: 18px 0 8px;
  text-align: center;
  color: #182433;
  font-size: 20px;
  font-weight: 700;
}

.kid-card__class {
  align-self: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: #fff2cf;
  color: #8a6318;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.kid-card__grade {
  margin-top: 8px;
  text-align: center;
  color: #63778d;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
}

.kid-card__meta {
  margin: 22px 0 20px;
}

.kid-card__meta-row {
  display: grid;
  grid-template-columns: 54px 1fr;
  gap: 10px;
  margin-bottom: 12px;
}

.kid-card__meta-row dt {
  margin: 0;
  color: #63778d;
  font-size: 14px;
  font-weight: 600;
}

.kid-card__meta-row dd {
  margin: 0;
  color: #1e2e40;
  font-size: 15px;
  font-weight: 700;
  text-align: right;
}

.kid-card__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 130px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.kid-card__status--default {
  background: #eef4fb;
  color: #507194;
}

.kid-card__status--info {
  background: #dff0ff;
  color: #1663a7;
}

.kid-card__status--warning {
  background: #fff0c9;
  color: #946809;
}

.kid-card__status--success {
  background: #d9f8dd;
  color: #21693a;
}

.kid-card__status--muted {
  background: #eceff4;
  color: #617182;
}

.kid-card__action {
  width: 100%;
  min-height: 48px;
  border: none;
  border-radius: 999px;
  background: #1367b4;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.kid-card__action:hover:not(:disabled) {
  opacity: 0.93;
  transform: translateY(-1px);
}

.kid-card__action:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.kid-card__action--busy {
  background: #5b8fbe;
}

.kid-card__actions {
  display: grid;
  gap: 10px;
  margin-top: auto;
}

.kid-card__action--secondary {
  background: transparent;
  border: 1px solid #1367b4;
  color: #1367b4;
}

.kid-card__action--outline {
  background: #fff;
  border: 1px solid #8fb3d8;
  color: #14589a;
}

.kid-card__action--danger {
  background: #d0342c;
}

.results-scroll-sentinel {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 72px;
}

.results-footer {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-height: 38px;
  padding: 5px 20px 6px;
  background: rgba(247, 251, 255, 0.96);
  border-top: 1px solid rgba(199, 215, 236, 0.9);
  backdrop-filter: blur(8px);
}

.results-footer__meta {
  margin: 0;
  color: #5e738b;
  font-size: 13px;
}

.report-modal {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(13, 29, 46, 0.45);
  backdrop-filter: blur(4px);
}

.report-modal__dialog {
  width: min(1100px, 100%);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  border-radius: 26px;
  background: #fff;
  box-shadow: 0 30px 80px rgba(9, 28, 47, 0.28);
  overflow: hidden;
}

.report-modal__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px 14px;
  border-bottom: 1px solid #d8e2ef;
}

.report-modal__title {
  margin: 0;
  color: #16375d;
  font-size: 24px;
  font-weight: 700;
}

.report-modal__subtitle {
  margin: 6px 0 0;
  color: #6b8096;
  font-size: 14px;
}

.report-modal__close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #eef4fb;
  color: #33597a;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.report-modal__actions {
  display: flex;
  justify-content: flex-end;
  padding: 14px 24px 0;
}

.report-modal__refresh {
  min-width: 140px;
  min-height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: 999px;
  background: #1367b4;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.report-modal__refresh:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.report-modal__body {
  padding: 18px 24px 24px;
  overflow: auto;
}

.report-modal__state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 180px;
  border: 1px dashed #c9d6e6;
  border-radius: 20px;
  color: #5f7691;
  font-size: 17px;
}

.report-table-wrap {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;
}

.report-table th,
.report-table td {
  padding: 12px 14px;
  border: 1px solid #d8e2ef;
  text-align: center;
}

.report-table tbody td:first-child,
.report-table tbody th:first-child,
.report-table__group-row th:first-child {
  text-align: left;
}

.report-table thead th {
  background: #eef5fc;
  color: #183c61;
  font-size: 13px;
  font-weight: 700;
}

.report-table__group-row th {
  background: #e3eefb;
  font-size: 14px;
  letter-spacing: 0.02em;
}

.report-table tbody td,
.report-table tbody th {
  color: #284158;
  font-size: 14px;
}

.report-table__service2-head,
.report-table__service2-col {
  border-left: 4px solid #c8d8eb !important;
}

.report-table__class-head,
.report-table tbody tr td:first-child,
.report-table__total th:first-child {
  border-right: 4px solid #c8d8eb;
}

.report-table__class-head {
  border-right: 4px solid #c8d8eb !important;
}

.report-table__total th {
  background: #f6f9fd;
  font-weight: 700;
  border-top: 4px solid #c8d8eb;
}

@media (max-width: 768px) {
  .kids-check-new-shell {
    height: 100%;
  }

  .search-hero__title {
    font-size: 20px;
  }

  .search-hero {
    padding: 10px 12px 10px;
  }

  .search-box {
    align-items: center;
    flex-direction: row;
    gap: 8px;
  }

  .search-box__input {
    width: auto;
    flex: 1 1 auto;
    min-height: 46px;
    font-size: 15px;
    min-width: 0;
  }

  .search-hero__header {
    flex-direction: column;
    justify-content: center;
    margin-bottom: 8px;
  }

  .search-hero__report-button {
    position: static;
    transform: none;
  }

  .search-hero__report-button {
    min-width: 116px;
    min-height: 36px;
  }

  .search-box__button {
    width: 44px;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    border-radius: 50%;
    flex: 0 0 44px;
  }

  .search-box__button-text {
    display: none;
  }

  .search-box__button-icon {
    display: inline;
  }

  .results-section {
    padding: 0;
  }

  .results-scroll-area {
    padding: 16px 12px 12px;
  }

  .results-footer {
    flex-direction: column;
    align-items: stretch;
    min-height: 34px;
    padding: 5px 12px 6px;
  }

  .results-footer__meta {
    text-align: center;
  }

  .report-modal {
    padding: 12px;
  }

  .report-modal__header,
  .report-modal__actions,
  .report-modal__body {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
